import React, { useCallback, useEffect, useState } from 'react'
import { useGetTabValues, useListTabs } from './hooks'
import { useGetFileInfo } from '../drive'
import {
  GDriveFileInfo,
  SheetTab,
  GoogleLoginButton,
} from 'infrastructure/googleDrive'
import { parseSheetIdOrUrl } from 'shared/utils/googleSheets'
import { Box, Button, Chip, MenuItem, Select, TextField } from '@mui/material'
import { InfoDisplay } from '../../../utility/Containers'

const parseable = ['application/vnd.google-apps.spreadsheet']

export function SpreadsheetSheetSelector({
  onSheetDataChange,
}: {
  onSheetDataChange: (data: string[][] | undefined) => void
}) {
  return (
    <Box>
      <GoogleLoginButton
        ParentComponent={LoginPrompt}
        LoggedInComponent={() => (
          <SpreadSheetSelectorComp onSheetDataChange={onSheetDataChange} />
        )}
      />
    </Box>
  )
}

function LoginPrompt({ children }: { children: React.ReactNode }) {
  return (
    <Box padding="10px">
      <Box>Google Auth Required To Access Spreadsheets</Box>
      {children}
    </Box>
  )
}

function SpreadSheetSelectorComp({
  onSheetDataChange,
}: {
  onSheetDataChange: (data: string[][] | undefined) => void
}) {
  const [spreadsheetIds, setSpreadsheetIds] = useState<{
    spreadsheetId: string
    gid: number | undefined
  }>()
  const [fileInfo, setFileInfo] = useState<
    GDriveFileInfo & { canParse: boolean }
  >()
  const [fileTabs, setFileTabs] = useState<SheetTab[]>()
  const [selectedTab, setSelectedTab] = useState<number>()
  const tabsGetter = useListTabs()
  const infoGetter = useGetFileInfo()
  const getSheetData = useGetTabValues()

  useEffect(() => {
    setFileInfo(undefined)
    if (!spreadsheetIds?.spreadsheetId) return
    const { spreadsheetId } = spreadsheetIds
    infoGetter(spreadsheetId).then((fileInfo) => {
      if (!fileInfo || 'error' in fileInfo) {
        setFileInfo(undefined)
      } else {
        setFileInfo({
          ...fileInfo,
          canParse: parseable.includes(fileInfo.mimeType),
        })
      }
    })
  }, [spreadsheetIds, infoGetter, setFileInfo])

  useEffect(() => {
    if (!spreadsheetIds) return
    const { spreadsheetId, gid } = spreadsheetIds
    setSelectedTab(undefined)
    if (!fileInfo?.canParse) return
    tabsGetter(spreadsheetId).then((tabs) => {
      setFileTabs(tabs)
      if (!tabs) return
      const selected = tabs.find((t) => t.sheetId === gid) ?? tabs[0]
      setSelectedTab(selected.sheetId)
    })
  }, [spreadsheetIds, fileInfo, tabsGetter, setFileTabs, setSelectedTab])

  const doGetSheetData = useCallback(() => {
    const selection = fileTabs?.find(({ sheetId }) => sheetId === selectedTab)
    if (!selection || !spreadsheetIds?.spreadsheetId) return
    getSheetData({
      spreadsheetId: spreadsheetIds.spreadsheetId,
      title: selection.title,
    }).then((data) => {
      onSheetDataChange(data?.values)
    })
  }, [
    selectedTab,
    spreadsheetIds?.spreadsheetId,
    fileTabs,
    onSheetDataChange,
    getSheetData,
  ])

  useEffect(doGetSheetData, [doGetSheetData])

  if (!spreadsheetIds?.spreadsheetId) {
    return (
      <Box>
        <GetFileId onIdsChange={setSpreadsheetIds} />
      </Box>
    )
  }

  return (
    <Box>
      <FileInfo
        fileInfo={fileInfo}
        onRemoveFile={() => setSpreadsheetIds(undefined)}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <SheetTabSelector
            tabs={fileTabs || []}
            onTabSelect={setSelectedTab}
            selectedTabId={selectedTab ?? 0}
          />
        </Box>
        <Button variant="outlined" onClick={doGetSheetData}>
          Retry
        </Button>
      </Box>
    </Box>
  )
}

function FileInfo({
  fileInfo,
  onRemoveFile,
}: {
  fileInfo?: GDriveFileInfo & { canParse: boolean }
  onRemoveFile: () => void
}) {
  if (!fileInfo) return null

  return (
    <Box sx={{ mb: 1 }}>
      <Chip
        label={fileInfo.name ?? 'NOT FOUND'}
        onDelete={onRemoveFile}
        sx={{ mb: 1 }}
      />
      {!fileInfo.canParse && (
        <InfoDisplay variant="error">
          <Box>{fileInfo.mimeType}</Box>
          <Box>The Google Document should be a spreadsheet.</Box>
        </InfoDisplay>
      )}
    </Box>
  )
}

function GetFileId({
  onIdsChange,
}: {
  onIdsChange: (ids: { spreadsheetId: string; gid: number | undefined }) => void
}) {
  const [newFileId, setNewFileId] = useState('')
  const { spreadsheetId, gid } = parseSheetIdOrUrl(newFileId)
  const onSet = () => {
    onIdsChange({ spreadsheetId, gid })
  }
  return (
    <Box>
      <Box>
        <TextField
          label="Paste Spreadsheet URL or ID"
          fullWidth={true}
          value={newFileId}
          onChange={(e) => setNewFileId(e.target.value)}
        />
        <Button disabled={!spreadsheetId} onClick={onSet}>
          Get Sheet
        </Button>
      </Box>
    </Box>
  )
}

function SheetTabSelector({
  tabs,
  selectedTabId,
  onTabSelect,
}: {
  tabs: SheetTab[]
  selectedTabId: number
  onTabSelect: (tabId: number) => void
}) {
  return (
    <Select
      fullWidth
      variant="outlined"
      value={selectedTabId ?? tabs[0]?.sheetId ?? ''}
      onChange={(e) => onTabSelect(e.target.value as number)}
    >
      {tabs.map(({ title, sheetId }) => {
        return (
          <MenuItem key={sheetId} value={sheetId}>
            {title}
          </MenuItem>
        )
      })}
    </Select>
  )
}
